import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trophy, Users, Calendar, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CREATOR_EMAIL = 'danilogallo16@gmail.com';

const TournamentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const isCreator = user?.email === CREATOR_EMAIL;

  useEffect(() => { fetchTournaments(); }, []);

  const fetchTournaments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tournaments`, { withCredentials: true });
      setTournaments(res.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const statusLabel = (s) => {
    if (s === 'registration') return { text: 'Iscrizioni Aperte', cls: 'bg-green-100 text-green-700' };
    if (s === 'active') return { text: 'In Corso', cls: 'bg-amber-100 text-amber-700' };
    return { text: 'Completato', cls: 'bg-slate-100 text-slate-600' };
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto animate-fadeIn" data-testid="tournaments-page">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-outfit text-2xl font-semibold text-slate-900">Tornei</h1>
          <p className="text-slate-500 text-sm">Tornei del circolo</p>
        </div>
        {isCreator && (
          <Link
            to="/tournaments/new"
            data-testid="create-tournament-btn"
            className="inline-flex items-center gap-2 bg-amber-500 text-white rounded-full px-5 py-2.5 font-medium hover:bg-amber-600 transition-all"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Nuovo</span>
          </Link>
        )}
      </div>

      {tournaments.length === 0 ? (
        <div className="bg-white rounded-3xl p-8 border border-slate-100 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-amber-500" />
          </div>
          <h3 className="font-outfit text-lg font-medium text-slate-900 mb-2">Nessun torneo</h3>
          <p className="text-slate-500">Non ci sono ancora tornei programmati</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tournaments.map((t) => {
            const st = statusLabel(t.status);
            return (
              <Link
                key={t.tournament_id}
                to={`/tournaments/${t.tournament_id}`}
                data-testid={`tournament-card-${t.tournament_id}`}
                className="block bg-white rounded-2xl p-5 border border-slate-100 hover:border-amber-200 hover:-translate-y-0.5 hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-outfit font-semibold text-slate-900">{t.name}</h3>
                      <p className="text-xs text-slate-500">{t.gender === 'M' ? 'Maschile' : 'Femminile'}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                    {st.text}
                  </span>
                  <span className="text-slate-500 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {t.participants?.length || 0} iscritti
                  </span>
                  {t.winner_id && (
                    <span className="text-amber-600 font-medium text-xs flex items-center gap-1">
                      <Trophy className="w-3.5 h-3.5" />
                      Completato
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TournamentsPage;
